import { useQuery } from "@tanstack/react-query";
import { useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";
import { subDays, subHours, format } from "date-fns";

interface UserActivityData {
  time: string;
  activeUsers: number;
  logins: number;
  actions: number;
}

interface ActivityStats {
  currentActiveUsers: number;
  totalLoginsToday: number;
  peakUsers: number;
  averageSessionTime: number;
}

export const useRealtimeActivity = (timeRange: string, refreshInterval: number) => {
  const getTimeFilter = () => {
    const now = new Date();
    switch (timeRange) {
      case "1h":
        return subHours(now, 1);
      case "4h":
        return subHours(now, 4);
      case "24h":
        return subDays(now, 1);
      case "7d":
        return subDays(now, 7);
      case "30d":
        return subDays(now, 30);
      default:
        return subDays(now, 1);
    }
  };

  // User activity over time
  const { data: activityData, refetch: refetchActivity } = useQuery({
    queryKey: ["user-activity", timeRange],
    queryFn: async (): Promise<UserActivityData[]> => {
      const timeFilter = getTimeFilter();
      
      // Get login data
      const { data: loginData, error: loginError } = await supabase
        .from("login_logs")
        .select("created_at, user_name")
        .gte("created_at", timeFilter.toISOString())
        .order("created_at", { ascending: true });

      if (loginError) throw loginError;

      // Get session data for active users
      const { data: sessionData, error: sessionError } = await supabase
        .from("auth_sessions")
        .select("created_at, expires_at, is_active")
        .eq("is_active", true)
        .gte("created_at", timeFilter.toISOString());

      if (sessionError) throw sessionError;

      // Group by time intervals
      const getTimeInterval = () => {
        switch (timeRange) {
          case "1h":
          case "4h":
            return 15; // 15 minute intervals
          case "24h":
            return 60; // 1 hour intervals
          case "7d":
          case "30d":
            return 1440; // 1 day intervals
          default:
            return 60;
        }
      };

      const intervalMinutes = getTimeInterval();
      const intervals: Record<string, UserActivityData> = {};

      // Process login data
      loginData.forEach((login) => {
        const loginTime = new Date(login.created_at!);
        const intervalStart = new Date(
          Math.floor(loginTime.getTime() / (intervalMinutes * 60 * 1000)) * intervalMinutes * 60 * 1000
        );
        
        const timeKey = intervalStart.toISOString();
        const timeLabel = timeRange === "7d" || timeRange === "30d" 
          ? format(intervalStart, "dd/MM")
          : intervalStart.toLocaleTimeString('nl-NL', { hour: '2-digit', minute: '2-digit' });
        
        if (!intervals[timeKey]) {
          intervals[timeKey] = {
            time: timeLabel,
            activeUsers: 0,
            logins: 0,
            actions: 0
          };
        }
        
        intervals[timeKey].logins++;
      });

      // Estimate active users (rough calculation based on sessions)
      const now = new Date();
      sessionData.forEach((session) => {
        const sessionStart = new Date(session.created_at);
        const sessionEnd = new Date(session.expires_at);
        
        // For each interval, check if session was active
        Object.keys(intervals).forEach((timeKey) => {
          const intervalStart = new Date(timeKey);
          const intervalEnd = new Date(intervalStart.getTime() + intervalMinutes * 60 * 1000);
          
          if (sessionStart <= intervalEnd && sessionEnd >= intervalStart) {
            intervals[timeKey].activeUsers++;
          }
        });
      });

      return Object.values(intervals).sort((a, b) => {
        if (timeRange === "7d" || timeRange === "30d") {
          return a.time.localeCompare(b.time);
        }
        return new Date(`2000-01-01 ${a.time}`).getTime() - new Date(`2000-01-01 ${b.time}`).getTime();
      });
    },
    refetchInterval: refreshInterval * 1000,
  });

  // Activity statistics
  const { data: activityStats, refetch: refetchStats } = useQuery({
    queryKey: ["activity-stats"],
    queryFn: async (): Promise<ActivityStats> => {
      const now = new Date();
      const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
      
      // Current active sessions
      const { data: activeSessions, error: sessionError } = await supabase
        .from("auth_sessions")
        .select("id")
        .eq("is_active", true)
        .gt("expires_at", now.toISOString());

      if (sessionError) throw sessionError;

      // Today's logins
      const { data: todayLogins, error: loginError } = await supabase
        .from("login_logs")
        .select("id")
        .gte("created_at", today.toISOString());

      if (loginError) throw loginError;

      return {
        currentActiveUsers: activeSessions?.length || 0,
        totalLoginsToday: todayLogins?.length || 0,
        peakUsers: Math.max(activeSessions?.length || 0, 5), // Rough estimate
        averageSessionTime: 45 // Minutes - rough estimate
      };
    },
    refetchInterval: refreshInterval * 1000,
  });

  // Real-time subscriptions
  useEffect(() => {
    const loginChannel = supabase
      .channel('login-logs-realtime')
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'login_logs'
        },
        () => {
          refetchActivity();
          refetchStats();
        }
      )
      .subscribe();

    const sessionChannel = supabase
      .channel('auth-sessions-realtime')
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'auth_sessions'
        },
        () => {
          refetchActivity();
          refetchStats();
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(loginChannel);
      supabase.removeChannel(sessionChannel);
    };
  }, [refetchActivity, refetchStats]);

  return {
    activityData: activityData || [],
    activityStats: activityStats || {
      currentActiveUsers: 0,
      totalLoginsToday: 0,
      peakUsers: 0,
      averageSessionTime: 0
    },
    isLoading: !activityData || !activityStats
  };
};